import { calculateLineItem, calculateFormTotals, calculateDisplayTotals } from "common/utils/lineItemCalc";
import { useCustomForm } from "common/hooks/src/api/useCustomForm";
import { Button } from "components/button";
import { CardWithTitle } from "components/card/CardWithTitle";
import { InputField, ResizableInputField } from "components/fields/InputField";
import { useGetDevis, useUpdateDevis } from "modules/devis/api";
import {
  CreateDevisPayload,
  ReductionType,
  DevisLineItem,
  DEVIS_STATUS,
} from "modules/devis/types/devis.interface";
import { FormEvent, useEffect, useState } from "react";
import { useDocumentPreview } from "common/hooks/src/useDocumentPreview";
import "react-datepicker/dist/react-datepicker.css";
import { useParams } from "react-router-dom";
import { useGetArticles } from "modules/article/api";
import { SelectIntervenants } from "modules/devis/components";
import { SelectClient } from "modules/devis/components";
import { SelectStatus } from "modules/devis/components";
import { SelectArticle } from "modules/devis/components";
import { SelectReductionType } from "modules/devis/components";
import { useCreateCommandeFromDevis } from "modules/commande/api";
import { useGetCommandes } from "modules/commande/api";
import { CreateCommandePayload } from "modules/commande/types/commande.interface";
import { Mail } from "lucide-react";
import { SendEmailModal } from "modules/devis/components/src/sendEmailDevis";

const UpdateDevis = () => {
  const { id } = useParams<{ id: string }>();
  const { data: devis } = useGetDevis(id);
  const { mutate } = useUpdateDevis(id);
  const { mutate: createCommande } = useCreateCommandeFromDevis();
  const { refetch } = useGetCommandes(false, id);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  const { formValues, setFormValues } = useCustomForm<CreateDevisPayload>({
    clientId: "",
    dueDate: new Date(),
    interveners: [],
    status: DEVIS_STATUS.DRAFT,
    lineItems: [
      {
        articleId: "",
        priceHT: 0,
        quantity: 0,
        vatAmount: 0,
        reductionAmount: 0,
        reductionType: ReductionType.PERCENTAGE,
        totalTTC: 0,
        totalHT: 0,
        description: "",
      },
    ],
    totalTTC: 0,
    totalHT: 0,
  });

  const { data: articlesData } = useGetArticles(undefined);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutate(formValues);
  };

  const handleStatusChange = async (newStatus: DEVIS_STATUS) => {
    try {
      await mutate({ ...formValues, status: newStatus });

      if (newStatus === DEVIS_STATUS.ACCEPTED) {
        // Conversion du devis en commande
        const commandeData: CreateCommandePayload = {
          clientId: formValues.clientId,
          lineItems: formValues.lineItems.map((item) => ({
            articleId: item.articleId,
            description: item.description,
            quantity: item.quantity,
            vatAmount: item.vatAmount,
            total: item.totalTTC,
            reductionAmount: item.reductionAmount,
            reductionType: item.reductionType,
            totalTTC: item.totalTTC,
            totalHT: item.totalHT,
            checked: false,
            priceHT: item.priceHT,
          })),
          notes: `Commande générée depuis le devis #${formValues._id}`,
          totalTTC: formValues.totalTTC,
          totalHT: formValues.totalHT,
        };

        await createCommande(commandeData);
        await refetch();
      }

      // Log ou autres actions spécifiques
      console.log(`Statut changé à : ${newStatus}`);
    } catch (error) {
      console.error("Erreur lors de la mise à jour :", error);
    }
  };

  useEffect(() => {
    if (!devis) return;

    const allArticles = articlesData
      ? articlesData.pages.flatMap((page) => page.data)
      : [];

    const updatedLineItems = devis.lineItems.map((item) => {
      const matchedArticle = allArticles.find(
        (a) => a._id === item.articleId
      );

      return {
        ...item,
        type: matchedArticle?.type,
        description: item.description ?? "",
        priceTTC: item.totalTTC,
        quantity: Number(item.quantity) || 0,
        vatAmount: Number(item.vatAmount) || 0,
        reductionAmount: Number(item.reductionAmount) || 0,
        reductionType: item.reductionType || ReductionType.PERCENTAGE,
        totalTTC: Number(item.totalTTC) || 0,
        totalHT: Number(item.totalHT) || 0,
        priceHT: (() => {
          const stored = Number((item as any).unitPriceHT) || Number(item.priceHT) || 0;
          if (stored > 0) return stored;
          const qty = Number(item.quantity);
          const ht = Number(item.totalHT);
          return !Number(item.reductionAmount) && ht > 0 && qty > 0 ? ht / qty : 0;
        })(),
      };
    });

    setFormValues({
      ...devis,
      dueDate: new Date(devis.dueDate),
      interveners: devis.interveners || [],
      lineItems: updatedLineItems,
      clientId: devis.clientId,
      status: devis.status,
      totalTTC: devis.totalTTC,
      totalHT: devis.totalHT,
    });
  }, [devis, articlesData, setFormValues]);

  const handleAddLineItem = () => {
    setFormValues({
      ...formValues,
      lineItems: [
        ...formValues.lineItems,
        {
          articleId: "",
          quantity: 1,
          reductionType: ReductionType.PERCENTAGE,
          reductionAmount: 0,
          vatAmount: 20,
          priceHT: 0,
          description: "",
          totalTTC: 0,
          totalHT: 0,
        },
      ],
    });
  };

  const handleRemoveLineItem = (index: number) => {
    const updatedLineItems = formValues.lineItems.filter(
      (_, idx) => idx !== index
    );
    const totals = calculateFormTotals(updatedLineItems);
    setFormValues({
      ...formValues,
      lineItems: updatedLineItems,
      totalTTC: totals.totalTTC,
      totalHT: totals.totalHT,
    });
  };

  const handleLineItemChange = (
    index: number,
    field: keyof DevisLineItem,
    value: any
  ) => {
    const updatedLineItems = formValues.lineItems.map((item, idx) => {
      if (idx === index) {
        const newItem = { ...item, [field]: value };

        if (field === "articleId") {
          const allArticles =
            articlesData?.pages.flatMap((page) => page.data) || [];
          const selectedArticle = allArticles.find((a) => a._id === value);
          if (selectedArticle) {
            newItem.priceHT = selectedArticle.priceHT;
            newItem.description = selectedArticle.label;
          }
        }

        const calc = calculateLineItem({
          priceHT: Number(newItem.priceHT) || 0,
          quantity: Number(newItem.quantity) || 0,
          vatAmount: Number(newItem.vatAmount) || 0,
          reductionAmount: Number(newItem.reductionAmount) || 0,
          reductionType: newItem.reductionType,
        });

        newItem.totalHT = calc.totalHT;
        newItem.totalTTC = calc.totalTTC;

        return newItem;
      }
      return item;
    });

    const totals = calculateFormTotals(updatedLineItems);
    setFormValues((prev) => ({
      ...prev,
      lineItems: updatedLineItems,
      totalTTC: totals.totalTTC,
      totalHT: totals.totalHT,
    }));
  };

  const liveSummary = useDocumentPreview(formValues.lineItems);
  const { totalHT, totalRemise, totalVAT, totalHTFinal, totalTTC } = liveSummary ?? devis?.summary ?? calculateDisplayTotals(formValues.lineItems);

  // Function to download PDF from server using authenticated API
  const downloadPDF = async () => {
    if (!id) return;
    
    try {
      const { getDevisBackend } = await import('modules/devis/api/src/backends');
      const backend = await getDevisBackend();
      const pdfData = await backend.downloadDevis(id);
      
      // Create blob and download
      const blob = new Blob([pdfData], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `devis-${devis?.documentNumber.number || id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  return (
    <div>
      <SendEmailModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        quoteId={devis?.documentNumber.number || ""}
        clientEmail={devis?.client.email || ""}
        quoteDate={
          devis?.createdAt
            ? new Date(devis.createdAt).toLocaleDateString("fr-FR")
            : ""
        }
        quoteTotal={devis?.totalTTC.toString() || ""}
        clientName={devis?.client.name || ""}
        isQuote={true}
      />

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex items-center justify-end gap-2 pt-4">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
            onClick={downloadPDF}
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            PDF
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-blue-700"
            onClick={() => setIsEmailModalOpen(true)}
          >
            <Mail className="h-3.5 w-3.5" />
            Envoyer
          </button>
        </div>
        <CardWithTitle title="Formulaire Devis">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <SelectClient
              formValues={formValues}
              setFormValues={setFormValues}
              data={devis?.client}
            />
            <InputField
              variant="auth"
              label="Échéance"
              id="dueDate"
              type="date"
              extra="mb-3 w-full"
              placeholder="Sélectionnez la date d'échéance"
              value={formValues.dueDate.toISOString().split("T")[0]}
              onChange={(e) =>
                setFormValues({
                  ...formValues,
                  dueDate: new Date(e.target.value),
                })
              }
            />
            <SelectIntervenants
              formValues={formValues}
              setFormValues={setFormValues}
            />
            <SelectStatus
              value={formValues.status}
              onChange={handleStatusChange}
            />
          </div>
          <div className="mt-6">
            <h3 className="mb-4 text-xl font-semibold">Articles</h3>
            {formValues.lineItems.map((item, index) => (
              <div
                key={index}
                className="relative mb-4 rounded-lg border border-gray-100 bg-gray-50/50 p-4"
              >
                <button
                  type="button"
                  onClick={() => handleRemoveLineItem(index)}
                  className="absolute right-3 top-3 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7">
                  <div className="col-span-1">
                    <SelectArticle
                      value={item.articleId}
                      onChange={(articleId) =>
                        handleLineItemChange(index, "articleId", articleId)
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <InputField
                      variant="auth"
                      label="Prix HT"
                      id={`priceHT-${index}`}
                      type="number"
                      extra="mb-3"
                      placeholder="0.00"
                      value={item.priceHT}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          "priceHT",
                          parseFloat(e.target.value)
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <InputField
                      variant="auth"
                      label="Quantité"
                      id={`quantity-${index}`}
                      type="number"
                      extra="mb-3"
                      placeholder="0"
                      min={1}
                      value={item.quantity}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          "quantity",
                          parseFloat(e.target.value)
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <InputField
                      variant="auth"
                      label="TVA %"
                      id={`vatAmount-${index}`}
                      type="number"
                      extra="mb-3"
                      placeholder="20.00"
                      value={item.vatAmount || ""}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          "vatAmount",
                          parseFloat(e.target.value)
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <InputField
                      variant="auth"
                      label="Réduction"
                      id={`reductionAmount-${index}`}
                      type="number"
                      extra="mb-3"
                      placeholder="0.00"
                      value={item.reductionAmount || ""}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          "reductionAmount",
                          parseFloat(e.target.value)
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <SelectReductionType
                      value={item.reductionType}
                      onChange={(reductionType) =>
                        handleLineItemChange(
                          index,
                          "reductionType",
                          reductionType
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <InputField
                      variant="auth"
                      label="Total TTC"
                      id={`total-${index}`}
                      type="number"
                      extra="mb-3"
                      placeholder="0.00"
                      value={item.totalTTC}
                      disabled
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <ResizableInputField
                    variant="auth"
                    label="Description"
                    id={`description-${index}`}
                    extra="mb-3"
                    placeholder="Description de l'item"
                    value={item.description}
                    onChange={(e) =>
                      handleLineItemChange(index, "description", e.target.value)
                    }
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddLineItem}
              className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 py-2.5 text-xs font-medium text-gray-500 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Ajouter un article
            </button>
          </div>

          <div className="mt-6 flex justify-end text-left">
            <CardWithTitle title="Totaux">
              <div className="grid w-full grid-cols-2 gap-3 sm:w-80">
                {/* Ligne Total HT */}
                <div className="text-sm font-medium text-gray-600">
                  Total HT
                </div>
                <div className="text-right text-sm">
                  {totalHT.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} €
                </div>

                {/* Ligne Remise générale */}
                <div className="text-sm font-medium text-gray-600">
                  Remise générale
                </div>
                <div className="text-right text-sm">
                  {totalRemise.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")}{" "}
                  €
                </div>

                {/* Ligne Total HT Final */}
                <div className="text-sm font-medium text-gray-600">
                  Total HT Final
                </div>
                <div className="text-right text-sm">
                  {totalHTFinal
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}{" "}
                  €
                </div>

                {/* Ligne TVA */}
                <div className="text-sm font-medium text-gray-600">TVA</div>
                <div className="text-right text-sm">
                  {totalVAT.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} €
                </div>

                {/* Ligne Total TTC */}
                <div className="text-sm font-medium text-gray-600">
                  Total TTC
                </div>
                <div className="text-right text-sm font-semibold">
                  {totalTTC.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} €
                </div>
              </div>
            </CardWithTitle>
          </div>
        </CardWithTitle>
        <div className="mt-2 flex w-full justify-end">
          <div className="w-full max-w-xs">
            <Button onClick={handleSubmit} variant="primary">
              Mettre à jour le devis
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UpdateDevis;
